import { startLoading, finishLoading } from './ui/uiUtils';
import {
	BoardUser,
	BrandPalette,
	EmpathiseColour,
	EngineId,
	GenerateTask,
	GraphMode,
	GroundingEvidence,
	IdeateGroundingSnapshot,
	LikedItem,
	NCCluster,
	PositioningGenerationContext,
	PresentationInfo,
	SearchEntry,
	SearchMode,
	TopicAnalysis,
	UserFile,
	UserPersona,
} from '../../../src/commonTypes';
import { AnalysisKey, SubTaskId, SYNTHESISE_AI_TASKS } from '../../../src/commonConstants';
import { buildPersonaImagePrompt } from '../../../src/ai/personaImagePrompt';

const boardSessionHeaders = (): Record<string, string> => {
	if (typeof document === 'undefined') return {};
	const token = document.body?.dataset.authToken?.trim();
	return token ? { Authorization: `Bearer ${token}` } : {};
};

const getPostOptions = (parameters: { [paramName: string]: string }) => {
	return {
		method: 'POST',
		credentials: 'same-origin' as const,
		headers: boardSessionHeaders(),
		body: new URLSearchParams(parameters),
	};
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const getPostJsonOptions = (jsonData: any) => {
	return {
		method: 'POST',
		credentials: 'same-origin' as const,
		headers: { ...boardSessionHeaders(), 'Content-Type': 'application/json' },
		body: JSON.stringify(jsonData),
	};
};

const sessionErrorFromResponse = async (response: Response): Promise<Error> => {
	let message = 'Session expired. Please sign in again.';
	try {
		const body = (await response.clone().json()) as { message?: string };
		if (body.message) message = body.message;
	} catch {
		// A legacy server may still return the login page. Keep the safe message.
	}
	return new Error(message);
};

/** Search endpoints may return 404 with a plain-text body when there are no matches. */
export type BoardSearchClientResult = {
	entries: Array<SearchEntry>;
	sensitiveFilteredCount: number;
	removedSensitive: Array<SearchEntry>;
	sensitivityExplanation: string;
	deferredGdelt?: boolean;
	sourceDiagnostics?: Array<{
		source: string;
		status: 'ok' | 'empty' | 'failed';
		entries: number;
		error?: string;
	}>;
	availableLenses?: Array<{
		id: string;
		label: string;
		purpose: string;
		preferredSources: string[];
	}>;
	selectedLensId?: string;
};

const emptySearchClientResult = (): BoardSearchClientResult => ({
	entries: [],
	sensitiveFilteredCount: 0,
	removedSensitive: [],
	sensitivityExplanation: '',
	deferredGdelt: false,
	sourceDiagnostics: [],
});

const readSearchResponse = async (response: Response): Promise<BoardSearchClientResult> => {
	if (response.status === 404) {
		return emptySearchClientResult();
	}
	if (response.status === 401) {
		throw await sessionErrorFromResponse(response);
	}
	if (!response.ok) {
		throw new Error(`Search failed (${response.status})`);
	}
	const contentType = response.headers.get('content-type') || '';
	if (!contentType.includes('json')) {
		return emptySearchClientResult();
	}
	const body = (await response.json()) as
		| Array<SearchEntry>
		| {
				entries?: Array<SearchEntry>;
				sensitiveFilteredCount?: number;
				removedSensitive?: Array<SearchEntry>;
				sensitivityExplanation?: string;
				deferredGdelt?: boolean;
				sourceDiagnostics?: BoardSearchClientResult['sourceDiagnostics'];
				availableLenses?: BoardSearchClientResult['availableLenses'];
				selectedLensId?: string;
		  };
	if (Array.isArray(body)) {
		return {
			entries: body,
			sensitiveFilteredCount: 0,
			removedSensitive: [],
			sensitivityExplanation: '',
		};
	}
	return {
		entries: Array.isArray(body.entries) ? body.entries : [],
		sensitiveFilteredCount: body.sensitiveFilteredCount || 0,
		removedSensitive: Array.isArray(body.removedSensitive) ? body.removedSensitive : [],
		sensitivityExplanation: body.sensitivityExplanation || '',
		deferredGdelt: Boolean((body as { deferredGdelt?: boolean }).deferredGdelt),
		sourceDiagnostics: Array.isArray(body.sourceDiagnostics) ? body.sourceDiagnostics : [],
		availableLenses: Array.isArray(body.availableLenses) ? body.availableLenses : [],
		selectedLensId: typeof body.selectedLensId === 'string' ? body.selectedLensId : 'all',
	};
};

/**
 * Call spark in the API
 */
export const spark = async (
	boardId: string,
	label: string
): Promise<{ result: Array<TopicAnalysis>; isFromTopic: boolean }> => {
	const response = await fetch(`/board/${boardId}/spark`, getPostOptions({ label }));
	return response.json() as unknown as { result: Array<TopicAnalysis>; isFromTopic: boolean };
};

export const enrichSparkPhrases = async (
	boardId: string,
	payload: {
		seedLabel: string;
		sliderValue: number;
		items: TopicAnalysis[];
		contextPath?: string[];
	}
): Promise<TopicAnalysis[]> => {
	const response = await fetch(
		`/board/${boardId}/tool-phrases/enrich`,
		getPostJsonOptions({
			tool: 'spark',
			...payload,
		})
	);
	if (!response.ok) {
		throw new Error('Unable to enrich spark phrases');
	}
	const body = (await response.json()) as { tool: 'spark'; result: TopicAnalysis[] };
	return body.result;
};

export const enrichEmpathisePhrases = async (
	boardId: string,
	payload: {
		seedLabel: string;
		sliderValue: number;
		lanes: Array<{ color: EmpathiseColour; values: TopicAnalysis[] }>;
		contextPath?: string[];
	}
): Promise<Array<{ color: EmpathiseColour; values: TopicAnalysis[] }>> => {
	const response = await fetch(
		`/board/${boardId}/tool-phrases/enrich`,
		getPostJsonOptions({
			tool: 'empathise',
			...payload,
		})
	);
	if (!response.ok) {
		throw new Error('Unable to enrich empathise phrases');
	}
	const body = (await response.json()) as {
		tool: 'empathise';
		result: Array<{ color: EmpathiseColour; values: TopicAnalysis[] }>;
	};
	return body.result;
};

export const enrichOnboardingPhrases = async (
	boardId: string,
	payload: {
		anchorText: string;
		input?: string;
		vision?: string;
		summary?: string;
	}
): Promise<{ centralTitle: string; phrases: TopicAnalysis[]; useCase?: string }> => {
	const response = await fetch(`/board/${boardId}/onboarding-phrases`, getPostJsonOptions(payload));
	if (!response.ok) {
		throw new Error('Unable to enrich onboarding phrases');
	}
	return response.json() as Promise<{
		centralTitle: string;
		phrases: TopicAnalysis[];
		useCase?: string;
	}>;
};

export const undo = async (boardId: string): Promise<boolean> => {
	const response = await fetch(`/board/${boardId}/undo`, getPostOptions({}));
	const result = (await response.json()) as unknown as { success: boolean };
	return result.success;
};

/**
 * Call Synthesise AI in the API
 */

type TaskId = keyof typeof SYNTHESISE_AI_TASKS | 'Vision' | 'ProjectSummary' | 'ProjectFraming';

export const synthesiseAi = async (
	boardId: string,
	task: TaskId | SubTaskId,
	prompt: string,
	imageUrl: string = ''
): Promise<{ result: string }> => {
	const response = await fetch(
		`/board/${boardId}/synthesise`,
		getPostOptions({ task, prompt, imageUrl })
	);
	if (response.status !== 200) {
		throw new Error('Unable to generate');
	}
	const result = (await response.json()) as unknown as { result: string };

	// If the task is Vision or Summary, update the board settings
	if (task === 'Vision' || task === 'Extract' || task === 'ProjectSummary') {
		await updateBoardVisionAndSummary(
			boardId,
			'', // input remains unchanged
			task === 'Vision' ? result.result : '', // update vision if task is Vision
			task === 'Extract' || task === 'ProjectSummary' ? result.result : '' // update summary
		);
	}

	return result;
};

export const synthesiseProjectFraming = async (
	boardId: string,
	prompt: string
): Promise<{ vision: string; summary: string }> => {
	const response = await fetch(
		`/board/${boardId}/synthesise`,
		getPostOptions({ task: 'ProjectFraming', prompt, imageUrl: '' })
	);
	if (response.status !== 200) {
		throw new Error('Unable to generate project framing');
	}
	const result = (await response.json()) as unknown as {
		result: string;
		vision?: string;
		summary?: string;
	};
	if (typeof result.vision === 'string' && typeof result.summary === 'string') {
		return { vision: result.vision, summary: result.summary };
	}
	const parsed = JSON.parse(result.result) as unknown;
	if (
		parsed &&
		typeof parsed === 'object' &&
		typeof (parsed as { vision?: unknown }).vision === 'string' &&
		typeof (parsed as { summary?: unknown }).summary === 'string'
	) {
		return {
			vision: (parsed as { vision: string }).vision,
			summary: (parsed as { summary: string }).summary,
		};
	}
	throw new Error('Unable to parse project framing');
};

/**
 * Call generate in the API
 */
export const generate = async (
	boardId: string,
	type: GenerateTask,
	label: string,
	resultCount?: number
): Promise<Array<{ snippet: string; title: string }>> => {
	const response = await fetch(
		`/board/${boardId}/generate`,
		getPostOptions({
			searchTerm: label,
			type,
			...(resultCount ? { resultCount: String(resultCount) } : {}),
		})
	);
	if (response.status !== 200) {
		throw new Error('Unable to generate');
	}
	return response.json() as unknown as Array<{ snippet: string; title: string }>;
};

export type GenerateProgressEffect = {
	stage: 'searching' | 'sources' | 'social' | 'grounding' | 'generating';
	detail: string;
	sources?: Array<{ title: string; source: string; url?: string }>;
};

export type GenerateRequestOptions = {
	signal?: AbortSignal;
	groundingEvidence?: GroundingEvidence[];
	skipLiveSources?: boolean;
	positioningContext?: PositioningGenerationContext;
};

const readGenerateNdjsonStream = async <T>(
	response: Response,
	onProgress?: (effect: GenerateProgressEffect) => void
): Promise<T | undefined> => {
	if (!response.body) return undefined;
	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	let buffer = '';
	let payload: T | undefined;
	let streamComplete = false;
	while (!streamComplete) {
		const { value, done } = await reader.read();
		streamComplete = done;
		buffer += decoder.decode(value, { stream: !done });
		const lines = buffer.split('\n');
		buffer = lines.pop() || '';
		for (const line of lines) {
			if (!line.trim()) continue;
			const event = JSON.parse(line) as {
				type: 'progress' | 'results' | 'error';
				effect?: GenerateProgressEffect;
				results?: T;
				evidence?: T;
				message?: string;
			};
			if (event.type === 'progress' && event.effect) onProgress?.(event.effect);
			if (event.type === 'results') {
				payload = (event.results !== undefined ? event.results : event.evidence) as T;
			}
			if (event.type === 'error') throw new Error(event.message || 'Unable to generate');
		}
	}
	return payload;
};

export type CollectGenerateGroundingResult = {
	evidence: GroundingEvidence[];
	groundingSnapshot?: IdeateGroundingSnapshot;
};

const parseCollectGenerateGroundingPayload = (
	payload: unknown
): CollectGenerateGroundingResult => {
	if (Array.isArray(payload)) {
		return { evidence: payload as GroundingEvidence[] };
	}
	if (payload && typeof payload === 'object') {
		const candidate = payload as {
			evidence?: unknown;
			groundingSnapshot?: IdeateGroundingSnapshot;
		};
		if (Array.isArray(candidate.evidence)) {
			return {
				evidence: candidate.evidence as GroundingEvidence[],
				groundingSnapshot: candidate.groundingSnapshot,
			};
		}
	}
	return { evidence: [] };
};

export const collectGenerateGrounding = async (
	boardId: string,
	type: GenerateTask,
	label: string,
	signal?: AbortSignal,
	options: Pick<GenerateRequestOptions, 'positioningContext' | 'skipLiveSources'> & {
		onProgress?: (effect: GenerateProgressEffect) => void;
	} = {}
): Promise<CollectGenerateGroundingResult> => {
	const shouldStream = Boolean(options.onProgress);
	const response = await fetch(`/board/${boardId}/generate`, {
		...getPostOptions({
			searchTerm: label,
			type,
			groundingOnly: 'true',
			...(shouldStream ? { stream: 'true' } : {}),
			...(options.skipLiveSources ? { skipLiveSources: 'true' } : {}),
			...(options.positioningContext
				? { positioningContext: JSON.stringify(options.positioningContext) }
				: {}),
		}),
		signal,
	});
	if (!response.ok) {
		const message = (await response.text()).trim() || 'Unable to collect grounding evidence';
		throw new Error(message);
	}
	if (shouldStream) {
		const streamed = await readGenerateNdjsonStream<CollectGenerateGroundingResult>(
			response,
			options.onProgress
		);
		return parseCollectGenerateGroundingPayload(streamed);
	}
	const parsed = (await response.json()) as unknown;
	return parseCollectGenerateGroundingPayload(parsed);
};

export const generateWithProgress = async (
	boardId: string,
	type: GenerateTask,
	label: string,
	resultCount: number | undefined,
	onProgress: (effect: GenerateProgressEffect) => void,
	options: GenerateRequestOptions = {}
): Promise<Array<{ snippet: string; title: string }>> => {
	const requestParameters = {
		searchTerm: label,
		type,
		...(resultCount ? { resultCount: String(resultCount) } : {}),
		stream: 'true',
		...(options.groundingEvidence !== undefined
			? { groundingEvidence: JSON.stringify(options.groundingEvidence) }
			: {}),
		...(options.skipLiveSources ? { skipLiveSources: 'true' } : {}),
		...(options.positioningContext
			? { positioningContext: JSON.stringify(options.positioningContext) }
			: {}),
	};
	const response = await fetch(`/board/${boardId}/generate`, {
		...getPostOptions(requestParameters),
		signal: options.signal,
	});
	if (!response.ok || !response.body) throw new Error('Unable to generate');
	const results = await readGenerateNdjsonStream<Array<{ snippet: string; title: string }>>(
		response,
		onProgress
	);
	return results || [];
};

const getPersonaText = (userPersona: UserPersona): string => buildPersonaImagePrompt(userPersona);
export const userPersonaImage = async (
	boardId: string,
	persona: UserPersona
): Promise<{ snippet: string; title: string }> => {
	startLoading('Generating persona image...');
	const personaText = getPersonaText(persona);
	const response = await fetch(
		`/board/${boardId}/user-persona-images`,
		getPostOptions({ personaText })
	);
	finishLoading();
	if (response.status !== 200) {
		throw new Error('Unable to generate');
	}
	return response.json() as unknown as { snippet: string; title: string };
};

export const motivationsStakeholderImage = async (
	boardId: string,
	name: string,
	description: string,
	title: string
): Promise<{ imageUrl: string }> => {
	const response = await fetch(
		`/board/${boardId}/motivations-images`,
		getPostOptions({ name, description, title })
	);
	if (response.status !== 200) {
		throw new Error('Unable to generate');
	}
	return response.json() as unknown as { imageUrl: string };
};

export type FilterOptions = 'Filter0' | 'Filter1' | 'Filter2' | 'Filter3' | 'Filter4';

/**
 * Call empathise in the API
 */
export const empathise = async (
	boardId: string,
	filter: FilterOptions,
	label: string
): Promise<{
	result: Array<{ color: EmpathiseColour; values: TopicAnalysis[] }>;
	isFromTopic: boolean;
}> => {
	const response = await fetch(
		`/board/${boardId}/empathise`,
		getPostOptions({
			label,
			filter,
		})
	);
	const results = (await response.json()) as Promise<{
		result: Array<{ color: EmpathiseColour; values: TopicAnalysis[] }>;
		isFromTopic: boolean;
	}>;
	return results;
};

/**
 * Call the API linker
 */
export const linker = async (boardId: string, word: Array<string>): Promise<string> => {
	const response = await fetch(
		`/board/${boardId}/linker`,
		getPostOptions({
			word: word.join(' '),
		})
	);
	const { suggestion } = (await response.json()) as { suggestion: string };
	return suggestion;
};

/**
 * Call the search API
 */
export const search = async (
	boardId: string,
	label: string,
	searchMode: SearchMode = 'mixed',
	deferredSource?: 'gdelt',
	lensId?: string
): Promise<BoardSearchClientResult> => {
	const response = await fetch(
		`/board/${boardId}/search`,
		getPostOptions({
			searchTerm: label,
			searchMode,
			...(deferredSource ? { deferredSource } : {}),
			...(lensId ? { lensId } : {}),
		})
	);
	return readSearchResponse(response);
};

export const splitTextIntoWords = async (
	boardId: string,
	text: string,
	mode: 'keywords' | 'auto' | 'topics' = 'auto'
): Promise<Array<string>> => {
	if (mode === 'topics') {
		startLoading(`Identifying key topics...`);
	}
	const response = await fetch(
		`/board/${boardId}/split-words`,
		getPostOptions({
			text,
			mode,
		})
	);
	if (mode === 'topics') {
		finishLoading();
	}
	return ((await response.json()) as unknown as { words: Array<string> }).words;
};

export const searchImage = async (
	boardId: string,
	src: string,
	searchMode: SearchMode = 'visual_similarity'
): Promise<BoardSearchClientResult> => {
	const response = await fetch(
		`/board/${boardId}/search`,
		getPostOptions({
			searchSrc: src,
			searchMode,
		})
	);
	return readSearchResponse(response);
};

/**
 * Like or unlike something, record this via API
 */
export const like = async (
	boardId: string,
	type: LikedItem['type'],
	text: string | null,
	url: string | null,
	remove: boolean,
	title?: string,
	topics?: string[],
	nodeUid?: string,
	positioningContext?: LikedItem['positioningContext'],
	clientId?: string
): Promise<void> => {
	const requestData = {
		boardId,
		type,
		remove,
		topics: topics?.join(','),
	} as {
		boardId: string;
		type: LikedItem['type'];
		text?: string;
		url?: string;
		title?: string;
		topics: string;
		nodeUid?: string;
		positioningContext?: string;
		clientId?: string;
		sharedToTeam?: string;
	};
	if (clientId) {
		requestData.clientId = clientId;
	}
	if (text) {
		requestData.text = text;
	}
	if (url) {
		requestData.url = url;
	}
	if (title) {
		requestData.title = title;
	}
	if (nodeUid) {
		requestData.nodeUid = nodeUid;
	}
	if (positioningContext) {
		requestData.positioningContext = JSON.stringify(positioningContext);
	}
	requestData.sharedToTeam = window.location.hash.includes('team') ? 'true' : 'false';
	await fetch(`/board/${boardId}/user-likes`, getPostOptions(requestData));
};

export const updateUserPersonaLike = async (
	boardId: string,
	likeId: string,
	persona: UserPersona
): Promise<LikedItem> => {
	const response = await fetch(`/board/${boardId}/user-likes/${likeId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ text: JSON.stringify(persona), title: persona.name }),
	});
	if (!response.ok) throw new Error(await response.text());
	return (await response.json()) as LikedItem;
};

export const updateUserPaletteLike = async (
	boardId: string,
	likeId: string,
	palette: BrandPalette
): Promise<LikedItem> => {
	const response = await fetch(`/board/${boardId}/user-likes/${likeId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ text: JSON.stringify(palette), title: palette.name }),
	});
	if (!response.ok) throw new Error(await response.text());
	return (await response.json()) as LikedItem;
};

export const updateUserTextLike = async (
	boardId: string,
	likeId: string,
	text: string,
	title: string
): Promise<LikedItem> => {
	const response = await fetch(`/board/${boardId}/user-likes/${likeId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ text, title }),
	});
	if (!response.ok) throw new Error(await response.text());
	return (await response.json()) as LikedItem;
};

export const shareLikeToTeam = async (boardId: string, likeId: string): Promise<LikedItem> => {
	const response = await fetch(`/board/${boardId}/user-likes/${likeId}/share-to-team`, {
		method: 'POST',
	});
	if (!response.ok) throw new Error(await response.text());
	return (await response.json()) as LikedItem;
};

export const deletePositioningOutput = async (boardId: string, likeId: string): Promise<void> => {
	const response = await fetch(`/board/${boardId}/user-likes/${likeId}/positioning-output`, {
		method: 'DELETE',
	});
	if (!response.ok) throw new Error(await response.text());
};

export const setPositioningOutputLiked = async (
	boardId: string,
	likeId: string,
	liked: boolean
): Promise<{
	liked: boolean;
	likeCount: number;
	likedByUsers: NonNullable<LikedItem['likedByUsers']>;
}> => {
	const response = await fetch(`/board/${boardId}/user-likes/${likeId}/positioning-like`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ liked }),
	});
	if (!response.ok) throw new Error(await response.text());
	return (await response.json()) as {
		liked: boolean;
		likeCount: number;
		likedByUsers: NonNullable<LikedItem['likedByUsers']>;
	};
};

export const updateSavedPersonaImage = async (
	boardId: string,
	clientId: string,
	imageUrl: string
): Promise<LikedItem | null> => {
	const response = await fetch(`/board/${boardId}/user-likes-persona-image`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ clientId, imageUrl }),
	});
	if (response.status === 404) return null;
	if (!response.ok) throw new Error(await response.text());
	return (await response.json()) as LikedItem;
};

export const cache = async (
	boardId: string,
	type: LikedItem['type'],
	text: string | null,
	url: string | null,
	title?: string
): Promise<void> => {
	const requestData = {
		boardId,
		type,
	} as { boardId: string; type: LikedItem['type']; text?: string; url?: string; title?: string };
	if (text) {
		requestData.text = text;
	}
	if (url) {
		requestData.url = url;
	}
	if (title) {
		requestData.title = title;
	}
	await fetch(`/board/${boardId}/cached-items`, getPostOptions(requestData));
};

export const reorderLikes = async (
	boardId: string,
	type: LikedItem['type'],
	likes: Array<LikedItem>
): Promise<void> => {
	if (!boardId || !likes) {
		return;
	}

	await fetch(`/board/${boardId}/user-likes-order`, getPostJsonOptions({ type, likes }));
};

export const clusters = async (
	boardId: string,
	cluster: AnalysisKey,
	labels: string,
	showLoading = true,
	signal?: AbortSignal
): Promise<Array<NCCluster>> => {
	if (showLoading) startLoading();
	const response = await fetch(
		`/board/${boardId}/clusters`,
		{
			...getPostOptions({
				boardId,
				filter: 'Filter0',
				cluster,
				labels,
			}),
			signal,
		}
	);

	if (showLoading) finishLoading();
	const clusters = (await response.json()) as Array<NCCluster>;
	return clusters;
};

export const searchForUser = async (
	searchText: string
): Promise<Array<{ username: string; email: string; id: string }>> => {
	return await fetch(`/search/user?query=${encodeURIComponent(searchText)}`).then(
		async (results) =>
			(await results.json()) as Array<{ username: string; email: string; id: string }>
	);
};

export const toggleAi = async (boardId: string, aiEnabled: boolean): Promise<void> => {
	await fetch(
		`/board/${boardId}/user-settings`,
		getPostOptions({
			ai_enabled: `${aiEnabled ? 'true' : 'false'}`,
		})
	);
};

export const setTutorialShown = async (
	userId: string,
	tutorial: GraphMode | 'dashboard'
): Promise<void> => {
	await fetch(`/users/${userId}`, getPostJsonOptions({ shownTutorial: true, tutorial }));
};

export const setMiniTutorialHidden = async (userId: string, tutorial: GraphMode): Promise<void> => {
	await fetch(`/users/${userId}`, getPostJsonOptions({ hideMiniTutorial: true, tutorial }));
};

export const createSubscription = async (
	paypalPlanId: string,
	quantity?: string,
	customPlanCode?: string,
	referralCode?: string
): Promise<string> => {
	const response = await fetch(
		'/api/create-subscription',
		getPostJsonOptions({ paypalPlanId, quantity, customPlanCode, referralCode })
	);
	const { subscriptionId } = (await response.json()) as { subscriptionId: string };
	return subscriptionId;
};

export const changeSubscription = async (
	paypalPlanId: string,
	quantity?: string,
	customPlanCode?: string,
	immediate?: boolean
): Promise<{ approveLink: string; subscriptionId: string }> => {
	const response = await fetch(
		'/api/change-subscription',
		getPostJsonOptions({ paypalPlanId, quantity, customPlanCode, immediate })
	);
	if (response.status !== 200) {
		throw new Error(await response.text());
	}
	const data = (await response.json()) as {
		approveLink: string;
		subscriptionId: string;
	};
	return data;
};

export const newSubscriptionApproved = async (subscriptionId: string): Promise<void> => {
	await fetch('/api/new-subscription', getPostJsonOptions({ subscriptionId }));
};

export const freeTrailSignUp = async (emailAddress: string) => {
	startLoading('Signing up...');
	const response = await fetch(
		'/api/free-trial',
		getPostOptions({
			email: emailAddress,
		})
	);

	finishLoading();
	if (response.status === 400) {
		return await response.text();
	}
	return undefined;
};

export const updateSubscription = async (userId: string, subscriptionId: string): Promise<void> => {
	await fetch(`/users/${userId}`, getPostJsonOptions({ subscriptionId }));
};

export const updateGoogleToken = async (userId: string, googleToken: string): Promise<void> => {
	await fetch(`/users/${userId}`, getPostJsonOptions({ googleToken }));
};

export const updateUserFullName = async (userId: string, name: string): Promise<void> => {
	await fetch(`/users/${userId}`, getPostJsonOptions({ name }));
};

export const updateUserConnectors = async (
	userId: string,
	mcpConnectors: Array<{
		id: string;
		provider:
			| 'notion'
			| 'slack'
			| 'github'
			| 'supabase'
			| 'vercel'
			| 'gemini'
			| 'claude'
			| 'pubmed'
			| 'gdelt'
			| 'custom';
		label: string;
		authType: 'static_headers' | 'oauth2_pkce';
		status: 'not_connected' | 'configured' | 'connecting' | 'connected' | 'expired' | 'error';
		transport: 'streamable-http' | 'sse';
		serverUrl: string;
		requestHeaders?: Record<string, string>;
		enabled: boolean;
		scopes?: string[];
		allowedToolNames?: string[];
		lastError?: string;
	}>
): Promise<void> => {
	await fetch(`/users/${userId}`, getPostJsonOptions({ mcpConnectors }));
};

export const startUserConnectorOAuth = async (
	userId: string,
	connectorId: string
): Promise<{ redirectUrl: string }> => {
	const response = await fetch(`/users/${userId}/connectors/${connectorId}/connect`, {
		method: 'POST',
	});
	if (response.status !== 200) {
		throw new Error(await response.text());
	}
	return (await response.json()) as { redirectUrl: string };
};

export const disconnectUserConnector = async (
	userId: string,
	connectorId: string
): Promise<void> => {
	const response = await fetch(`/users/${userId}/connectors/${connectorId}/disconnect`, {
		method: 'POST',
	});
	if (response.status !== 200) {
		throw new Error(await response.text());
	}
};

export const cancelCurrentSubscription = async (userId: string): Promise<void> => {
	startLoading('Cancelling subscription...');
	const result = await fetch(`/users/${userId}/subscription`, {
		method: 'DELETE',
	});
	finishLoading();
	console.log(result.json());
};

export const suspendCurrentSubscription = async (userId: string): Promise<void> => {
	startLoading('Suspending subscription...');
	await fetch(`/users/${userId}/subscription`, getPostJsonOptions({ suspended: true }));
	finishLoading();
};

export const activateCurrentSubscription = async (userId: string): Promise<void> => {
	startLoading('Activating subscription...');
	await fetch(`/users/${userId}/subscription`, getPostJsonOptions({ activated: true }));
	finishLoading();
};

export const updateCompanyName = async (companyId: string, name: string): Promise<void> => {
	await fetch(
		`/companies/${companyId}`,
		getPostJsonOptions({
			name,
		})
	);
};

export const updateBoardTitle = async (boardId: string, boardTitle: string): Promise<void> => {
	startLoading('Updating board title...');
	await fetch(
		`/board/${boardId}/settings`,
		getPostOptions({
			boardTitle,
		})
	);
	finishLoading();
};

export const updateBoardEngine = async (boardId: string, engineId: EngineId): Promise<void> => {
	startLoading('Updating board engine...');
	await fetch(
		`/board/${boardId}/settings`,
		getPostOptions({
			engine: engineId,
		})
	);
	finishLoading();
};

export const updateBoardVisionAndSummary = async (
	boardId: string,
	input: string,
	vision: string,
	summary: string
): Promise<void> => {
	startLoading('Updating board input, vision and summary...');
	await fetch(
		`/board/${boardId}/settings`,
		getPostOptions({
			input,
			vision,
			summary,
		})
	);
	finishLoading();
};

export const getPresentationInfo = async (boardId: string): Promise<PresentationInfo> => {
	startLoading('Loading presentation preview...');
	const response = await fetch(`/board/${boardId}/presentation-info`);
	finishLoading();
	return (await response.json()) as PresentationInfo;
};

export const savePresentationInfo = async (
	boardId: string,
	presentation: PresentationInfo
): Promise<void> => {
	await fetch(`/board/${boardId}/presentation-info`, getPostJsonOptions(presentation));
};

export const deleteBoard = async (boardId: string): Promise<void> => {
	startLoading('Deleting board...');
	await fetch(`/board/${boardId}`, {
		method: 'DELETE',
	});
	finishLoading();
};

export const moveIntoProject = async (boardId: string, projectId: string): Promise<void> => {
	startLoading('Move into project...');
	await fetch(
		`/board/${boardId}/settings`,
		getPostOptions({
			projectId,
		})
	);
	finishLoading();
};

export const updateProjectTitle = async (
	projectId: string,
	projectTitle: string
): Promise<void> => {
	startLoading('Updating project title...');
	await fetch(
		`/project/${projectId}/settings`,
		getPostOptions({
			projectTitle,
		})
	);
	finishLoading();
};

export const deleteProjectFromDB = async (projectId: string): Promise<void> => {
	startLoading('Deleting project...');
	await fetch(`/project/${projectId}`, {
		method: 'DELETE',
	});
	finishLoading();
};

export const getUserLikes = async (
	boardId: string,
	overrideUserId?: string,
	isTeam = false
): Promise<Array<LikedItem>> => {
	let url = overrideUserId
		? `/board/${boardId}/user-likes/${overrideUserId}`
		: `/board/${boardId}/user-likes`;
	url = url + '?isTeam=' + isTeam;
	const response = await fetch(url, { cache: 'no-store' });
	if (!response.ok) {
		throw new Error(`Unable to load positioning outputs (${response.status})`);
	}

	return (await response.json()) as Array<LikedItem>;
};

export const getBoardImages = async (
	boardId: string
): Promise<Array<{ url: string; description: string }>> => {
	const response = await fetch(`/board/${boardId}/images`);
	return (await response.json()) as Array<{ url: string; description: string }>;
};

const readImageSaveResponse = async (response: Response, action: string): Promise<string> => {
	const contentType = response.headers.get('content-type') || '';
	const redirectedToLogin =
		response.redirected &&
		new URL(response.url || window.location.href, window.location.origin).pathname.startsWith('/login');
	if (response.status === 401 || redirectedToLogin) {
		if (response.status === 401) throw await sessionErrorFromResponse(response);
		throw new Error('Session expired. Please sign in again.');
	}
	if (!response.ok) {
		const responseText = (await response.text()).trim();
		throw new Error(`${action} failed (${response.status})${responseText ? `: ${responseText}` : ''}`);
	}
	if (!contentType.toLowerCase().includes('json')) {
		throw new Error(`${action} returned ${contentType || 'an unknown content type'} instead of JSON`);
	}

	const payload = (await response.json()) as { url?: string };
	if (!payload.url) throw new Error(`${action} returned no saved URL`);
	return payload.url;
};

export const uploadBoardImage = async (boardId: string, image: File): Promise<string> => {
	const formData = new FormData();
	formData.append('image', image);
	const response = await fetch(`/board/${boardId}/images`, {
		method: 'POST',
		credentials: 'same-origin',
		headers: boardSessionHeaders(),
		body: formData,
	});
	return readImageSaveResponse(response, 'Image upload');
};

export const saveBoardImageFromUrl = async (boardId: string, url: string): Promise<string> => {
	let remoteImportFailure = '';
	try {
		const importResponse = await fetch(
			`/board/${boardId}/images-from-web`,
			getPostOptions({ url })
		);
		if (importResponse.ok) {
			return await readImageSaveResponse(importResponse, 'Image import');
		}
		if (importResponse.status === 401) {
			throw await sessionErrorFromResponse(importResponse);
		}
		const failure = (await importResponse.text()).trim();
		remoteImportFailure = `server import ${importResponse.status}${failure ? `: ${failure}` : ''}`;
	} catch (error) {
		remoteImportFailure = error instanceof Error ? error.message : String(error);
		if (remoteImportFailure.startsWith('Session expired.')) throw error;
	}

	// Some hosts reject backend requests but allow the already validated proxy
	// request. Reuse that byte path and upload it as a normal board image.
	const playbackUrl =
		url.startsWith('http') && !url.includes(window.location.host)
			? `/export-images?responseType=blob&url=${encodeURIComponent(url)}`
			: url;
	try {
		const response = await fetch(playbackUrl, { cache: 'force-cache' });
		if (!response.ok) {
			throw new Error(`proxy download ${response.status}`);
		}
		const imageBlob = await response.blob();
		if (!imageBlob.size) throw new Error('proxy returned an empty image');
		if (imageBlob.size > 15 * 1024 * 1024) {
			throw new Error('image exceeds the 15 MB import limit');
		}
		const mimeType = imageBlob.type.startsWith('image/') ? imageBlob.type : 'image/jpeg';
		const extension =
			mimeType.split('/')[1]?.replace('jpeg', 'jpg').replace(/[^a-z0-9]/gi, '') || 'jpg';
		const imageFile = new File([imageBlob], `search-result.${extension}`, { type: mimeType });
		return await uploadBoardImage(boardId, imageFile);
	} catch (error) {
		const proxyFailure = error instanceof Error ? error.message : String(error);
		if (proxyFailure.startsWith('Session expired.')) throw error;
		throw new Error(
			`Unable to save search image (${remoteImportFailure || 'server import failed'}; ${proxyFailure})`
		);
	}
};

export const getBoardVideos = async (
	boardId: string
): Promise<Array<{ url: string; description: string }>> => {
	const response = await fetch(`/board/${boardId}/videos`);
	return (await response.json()) as Array<{ url: string; description: string }>;
};

export const removeUserFromBoard = async (boardId: string, userId: string): Promise<void> => {
	startLoading('Removing user...');
	await fetch(`/board/${boardId}/user/${userId}`, {
		method: 'DELETE',
	});
	finishLoading();
};

export const requestAccessToBoard = async (boardId: string, userId: string): Promise<void> => {
	startLoading('Requesting access...');
	await fetch(
		`/board/${boardId}/requests`,
		getPostOptions({
			userId,
		})
	);
	finishLoading();
};

export const decideAccessRequest = async (
	boardId: string,
	requestId: string,
	accept: boolean
): Promise<{
	success: boolean;
	message: string;
}> => {
	startLoading(accept ? 'Adding collaborator...' : 'Declining request...');
	console.log(`/board/${boardId}/requests/${requestId}`);
	const result = await fetch(
		`/board/${boardId}/requests/${requestId}`,
		getPostJsonOptions({
			accept,
		})
	).then(
		(res) =>
			res.json() as unknown as {
				success: boolean;
				message: string;
			}
	);
	finishLoading();
	return result;
};

export const inviteUserWithUsername = async (
	inviteAction: string,
	username: string
): Promise<{
	success: boolean;
	message: string;
	colour: string;
	username: string;
	email: string;
	id: string;
}> => {
	startLoading('Inviting user...');
	const result = await fetch(
		inviteAction,
		getPostOptions({
			username,
		})
	).then(
		(res) =>
			res.json() as unknown as {
				success: boolean;
				message: string;
				colour: string;
				username: string;
				email: string;
				id: string;
			}
	);
	finishLoading();
	return result;
};

export const addNonSignedUpMemberToTeam = async (companyId: string, email: string) => {
	startLoading('Inviting team member...');
	const result = await fetch(
		`/companies/${companyId}/members`,
		getPostOptions({
			email,
		})
	).then(
		(res) =>
			res.json() as unknown as {
				success: boolean;
				message: string;
				colour?: string;
			}
	);
	finishLoading();
	return result;
};

export const addMemberToTeam = async (companyId: string, username: string) => {
	startLoading('Adding team member...');
	const result = await fetch(
		`/companies/${companyId}/members`,
		getPostOptions({
			username,
		})
	).then(
		(res) =>
			res.json() as unknown as {
				success: boolean;
				message: string;
				colour?: string;
			}
	);
	finishLoading();
	return result;
};

export const removeMemberFromTeam = async (companyId: string, userId: string) => {
	startLoading('Removing team member...');
	await fetch(`/companies/${companyId}/members/${userId}`, {
		method: 'DELETE',
	});
	finishLoading();
};

export const removeMemberInviteFromTeam = async (companyId: string, inviteId: string) => {
	startLoading('Cancelling team member invite...');
	await fetch(`/companies/${companyId}/member-invites/${inviteId}`, {
		method: 'DELETE',
	});
	finishLoading();
};

export const inviteWithNonSignedUpEmail = async (
	inviteAction: string,
	email: string
): Promise<{
	success: boolean;
	message: string;
	colour: string;
	email: string;
	id: string;
}> => {
	startLoading('Sending invite...');
	const result = await fetch(
		inviteAction,
		getPostOptions({
			email,
		})
	).then(
		(res) =>
			res.json() as unknown as {
				success: boolean;
				message: string;
				colour: string;
				email: string;
				id: string;
			}
	);
	finishLoading();
	return result;
};

export const getFullSubscriptionPlan = async (planId: string, quantity: number) => {
	const response = await fetch(`/settings/plans/${planId}?quantity=${quantity}`, {
		method: 'GET',
	});
	return (await response.json()) as {
		id: string;
		price: number;
		hasQuantity: boolean;
		discount: number;
		frequency: 'MONTH' | 'YEAR';
	};
};

export const removeUserFromProject = async (projectId: string, userId: string): Promise<void> => {
	startLoading('Removing user...');
	await fetch(`/project/${projectId}/users/${userId}`, {
		method: 'DELETE',
	});
	finishLoading();
};

export const getChatbotSuggestions = async (
	currentEventType: string,
	userGoal: string,
	availableRules: object,
	board: string,
	timeSpent: number
) => {
	const response = await fetch('/chatbot/suggestions', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			currentEventType,
			userGoal,
			availableRules,
			board,
			timeSpent,
		}),
	});
	return (await response.json()) as {
		data: string;
	};
};

export const getInterestingNode = async (nodeLabels: string[], projectVision: string) => {
	const response = await fetch('/chatbot/interesting-node', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			nodeLabels,
			projectVision,
		}),
	});
	return (await response.json()) as {
		data: string;
	};
};

// Analyses the array of suggestions and returns the most interesting one based on the project vision
export const getInterestingSuggestion = async (
	suggestions: string[],
	projectVision: string,
	aiTool: string,
	activeNode: string,
	templateId: string
) => {
	const response = await fetch('/chatbot/interesting-suggestion', {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
		},
		body: JSON.stringify({
			suggestions,
			projectVision,
			aiTool,
			activeNode,
			templateId,
		}),
	});
	return (await response.json()) as {
		data: string;
	};
};

export const getPdfContextFromFileSearch = async (boardId: string): Promise<string> => {
	startLoading('Fetching context from the pdf...');

	const response = await fetch(`/board/${boardId}/contexts`, {
		method: 'GET',
	});

	console.log(response);

	const res = (await response.json()) as {
		context: string;
	};

	finishLoading();
	return res.context;
};

export type PdfAnalysisView = {
	analysisId?: string;
	fileId: string;
	fileName?: string;
	model?: string;
	status: 'queued' | 'extracting_text' | 'extracting_graph' | 'ready' | 'partial' | 'failed';
	publishedStatus?: 'ready' | 'partial';
	ingestionStatus?:
		| 'queued'
		| 'extracting_text'
		| 'extracting_graph'
		| 'ready'
		| 'partial'
		| 'failed';
	overview?: string;
	keywords?: Array<{ text: string; salience: number }>;
	pageCount?: number;
	extractedPageCount?: number;
	sourceAvailability?: 'local' | 'openai_only' | 'derived_only';
	reuploadRequired?: boolean;
	error?: { stage: string; message: string; retryable: boolean };
	extractionDiagnostics?: {
		inputChunkCount: number;
		selectedChunkCount: number;
		repairAttempted: boolean;
		repairSucceeded: boolean;
		repairedInsightCount: number;
		repairedRelationshipCount: number;
		quarantinedInsightCount: number;
		quarantinedRelationshipCount: number;
		issueCodes: string[];
		response?: {
			responseId?: string;
			requestId?: string;
			model?: string;
			status?: string;
			failureCode?:
				| 'api_error'
				| 'parse_failed'
				| 'refusal'
				| 'incomplete'
				| 'failed'
				| 'cancelled'
				| 'unexpected_status'
				| 'empty_structured_output';
			incompleteReason?: string;
			errorCode?: string;
			refusal?: string;
			inputCharacters: number;
			maxOutputTokens: number;
			inputTokens?: number;
			outputTokens?: number;
			totalTokens?: number;
		};
	};
	ingestionTimings?: {
		textExtractionMs?: number;
		embeddingMs?: number;
		graphExtractionMs?: number;
		publicationMs?: number;
		totalMs?: number;
	};
	insights?: Array<{
		insightId: string;
		label: string;
		description: string;
		kind: string;
		level?: number;
		salience: number;
		confidence: number;
		provenance: 'legacy_derived' | 'source_extracted' | 'source_inferred';
		evidence: Array<{ chunkId: string; page?: number }>;
	}>;
};

export const getPdfAnalysis = async (
	boardId: string,
	fileId?: string
): Promise<PdfAnalysisView> => {
	const response = await fetch(
		`/board/${boardId}/pdf-analysis${fileId ? `?fileId=${encodeURIComponent(fileId)}` : ''}`
	);
	if (!response.ok && response.status !== 202) {
		throw new Error('Unable to load PDF analysis');
	}
	return (await response.json()) as PdfAnalysisView;
};

export const retryPdfAnalysis = async (boardId: string, fileId?: string): Promise<void> => {
	const response = await fetch(
		`/board/${boardId}/pdf-analysis/retry${fileId ? `?fileId=${encodeURIComponent(fileId)}` : ''}`,
		{ method: 'POST' }
	);
	if (!response.ok) {
		const body = (await response.json().catch(() => ({}))) as { error?: string };
		throw new Error(body.error || 'Unable to retry PDF analysis');
	}
};

export type PdfGraphExpansionView = {
	analysisId?: string;
	seedInsightId?: string;
	results: Array<{
		label: string;
		insightId: string;
		kind: string;
		level?: 1 | 2 | 3;
		provenance: 'legacy_derived' | 'source_extracted' | 'source_inferred';
		evidence: Array<{ chunkId: string; page?: number; text: string }>;
		confidence: number;
		salience: number;
		keywords: string[];
	}>;
	diagnostics: {
		candidateCount: number;
		expansionMode?: 'top_level' | 'children';
		fallbackReason?: string;
	};
};

export const expandPdfGraph = async (
	boardId: string,
	input: { analysisId?: string; insightId?: string }
): Promise<PdfGraphExpansionView> => {
	startLoading('Expanding from document graph...');
	try {
		const response = await fetch(`/board/${boardId}/pdf-analysis/expand`, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(input),
		});
		const body = (await response.json()) as PdfGraphExpansionView;
		if (!response.ok && response.status !== 202) {
			throw new Error(
				body.diagnostics?.fallbackReason || `Unable to expand PDF graph (${response.status})`
			);
		}
		return body;
	} finally {
		finishLoading();
	}
};

export const uploadPdf = async (boardId: string, files: File[]): Promise<boolean> => {
	startLoading('Uploading PDF files...');

	const formData = new FormData();
	files.forEach((file) => formData.append('files', file));
	const response = await fetch(`/board/${boardId}/upload`, {
		method: 'POST',
		body: formData,
	});

	console.log(response);

	finishLoading();
	return response.status === 202;
};

export type PdfContextSourceView = {
	boardId: string;
	boardTitle: string;
	currentBoard: boolean;
	file: UserFile;
	analysis?: {
		status: string;
		ingestionStatus: string;
		overview?: string;
		keywords?: string[];
		analysisId: string;
	};
};

export type PdfContextView = {
	files: UserFile[];
	users: BoardUser[];
	context: Array<{ boardId: string; fileId: string }>;
	sources: PdfContextSourceView[];
	uploadStatus?: { state: string; message?: string };
};

export const getFilesOfBoard = async (boardId: string): Promise<PdfContextView> => {
	const response = await fetch(`/board/${boardId}/files`, {
		method: 'GET',
	});
	console.log(response);
	const res = (await response.json()) as PdfContextView;
	return res;
};

export const updatePdfContext = async (
	boardId: string,
	context: Array<{ boardId: string; fileId: string }>
): Promise<void> => {
	const response = await fetch(`/board/${boardId}/pdf-context`, getPostJsonOptions({ context }));
	if (!response.ok) {
		const body = (await response.json().catch(() => ({}))) as { error?: string };
		throw new Error(body.error || 'Unable to update PDF context');
	}
};

export const fileSelectionBoard = async (
	boardId: string,
	fileId: string
): Promise<{ files: UserFile[]; users: BoardUser[] }> => {
	const response = await fetch(`/board/${boardId}/files/${fileId}`, {
		method: 'GET',
	});
	console.log(response);
	const res = (await response.json()) as { files: UserFile[]; users: BoardUser[] };
	return res;
};
